import React, { useState, Component } from 'react';
import { MapPin, Clock, Info, Bike, ShoppingBag, UtensilsCrossed, Navigation, CalendarClock, X } from 'lucide-react';
import { Restaurant, RestaurantSettings } from '../../lib/types';
import { formatCurrency } from '../../lib/utils';
import { LanguageSelector } from '../ui/LanguageSelector';
import { useLanguage } from '../../lib/i18n';
import { isRestaurantOpenBySchedule } from '../../lib/schedule';
interface RestaurantHeaderProps {
  restaurant: Restaurant;
  settings: RestaurantSettings | null;
  zoneName?: string;
}
export function RestaurantHeader({
  restaurant,
  settings,
  zoneName
}: RestaurantHeaderProps) {
  const {
    t
  } = useLanguage();
  const [showSchedule, setShowSchedule] = useState(false);
  const scheduleStatus = isRestaurantOpenBySchedule(restaurant.slug);
  const isActuallyOpen = scheduleStatus.hasSchedule ? scheduleStatus.isOpen : false;
  const isEmilius = restaurant.slug === 'emilius-pizza';
  const isKabu = restaurant.slug === 'kabu';
  const isCongelados = restaurant.slug === 'heladeria-congelados';
  const isNatuCafe = restaurant.slug?.includes('natucafe') ?? false;
  const isCrackPollo = restaurant.slug === 'crack-pollo' || String(restaurant.id) === '20';
  const isCabanaParrillera = String(restaurant.id) === '24' || restaurant.slug === 'cabana-parrillera' || restaurant.slug === 'la-cabana-parrillera';
  const coverUrl = isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isCrackPollo ? 'https://cdn.magicpatterns.com/uploads/vkpirHwDmy1iyMJzGb5usA/ChatGPT_Image_30_abr_2026,_00_44_20.png' : restaurant.cover_url || 'https://cdn.magicpatterns.com/uploads/rYDkPPUtvynEvWDL4HujUk/portada.png';
  const logoUrl = isEmilius ? 'https://cdn.magicpatterns.com/uploads/9961Lp77JNxDVSaBa43esn/Screenshot_2026-04-05_at_11.27.46_AM.png' : isKabu ? 'https://cdn.magicpatterns.com/uploads/9XbgtFaH2YhrgJYD7LjTSs/Diseno_minimalista_con_logo_moderno.png' : isCongelados ? 'https://cdn.magicpatterns.com/uploads/24WhrxVDroFFjBpfomMhk3/ChatGPT_Image_26_abr_2026,_23_23_00.png' : isCrackPollo ? 'https://cdn.magicpatterns.com/uploads/vrdnrQseiRNenTxSZL7mGp/ChatGPT_Image_30_abr_2026,_00_42_25.png' : restaurant.logo_url;
  return <div className={`${isNatuCafe ? 'bg-[#0F0D0B] border-[#2A2420]' : isCongelados ? 'bg-[#FBF5FF] border-[#E8DDF0]' : isCabanaParrillera ? 'bg-[#0B0807] border-[#3A1F0F]' : 'bg-[#FBF7F2] border-[#E8DDD0]'} border-b`} data-id="element-1625">
      {/* Cover Image */}
      <div className={`relative h-44 sm:h-60 md:h-72 w-full ${isNatuCafe ? 'bg-[#1A1614]' : isCabanaParrillera ? 'bg-[#0B0807]' : 'bg-[#F5EDE3]'}`} data-id="element-1626">
        <img src={isCongelados ? 'https://cdn.magicpatterns.com/uploads/ar4FXudFfRgAEWhtKsiDih/ChatGPT_Image_22_abr_2026,_09_55_36.png' : coverUrl} alt={restaurant.name} className="w-full h-full object-cover" decoding="async" data-id="element-1627" />
        <div className={`absolute inset-0 bg-gradient-to-t ${isNatuCafe ? 'from-[#0F0D0B] via-black/50' : isCongelados ? 'from-[#FBF5FF] via-[#7B2D8E]/40' : isCabanaParrillera ? 'from-[#0B0807] via-[#1A0B05]/70' : 'from-[#FBF7F2] via-black/40'} to-transparent`} data-id="element-1628" />

        {/* Badges */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end" data-id="element-1629">
          <div className={`${isNatuCafe ? 'bg-[#1A1614]/90 border-[#2A2420]' : 'bg-white/90 border-' + (isCongelados ? '[#E8DDF0]' : '[#E8DDD0]')} border backdrop-blur-md rounded-lg shadow-lg`} data-id="element-1630">
            <LanguageSelector data-id="element-1631" />
          </div>
          {restaurant.is_featured && <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${isNatuCafe ? 'bg-[#C6A969] text-[#0F0D0B]' : isCongelados ? 'bg-[#7B2D8E]' : 'bg-[#8B5E3C]'} text-white shadow-lg`} data-id="element-1632">
              ★ {t('featured')}
            </span>}
          {!isActuallyOpen && <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${isNatuCafe ? 'bg-[#D4883A]' : isCongelados ? 'bg-[#F472B6]' : 'bg-[#CE1126]'} text-white shadow-lg`} data-id="element-1633">
              {t('closed')}
            </span>}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-1634">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-end -mt-10 sm:-mt-14 mb-5" data-id="element-1635">
          {/* Logo */}
          <div className={`relative w-20 h-20 sm:w-28 sm:h-28 rounded-2xl ${isNatuCafe ? 'bg-[#1A1614] border-[#2A2420]' : isCabanaParrillera ? 'bg-[#1A0B05] border-[#3A1F0F]' : 'bg-white border-' + (isCongelados ? '[#E8DDF0]' : '[#E8DDD0]')} p-1 shadow-md border shrink-0`} data-id="element-1636">
            {logoUrl ? <img src={logoUrl} alt={`${restaurant.name} logo`} className={`w-full h-full rounded-xl ${isNatuCafe ? 'object-contain bg-[#0F0D0B]' : isEmilius ? 'object-contain bg-[#F2E6D5]' : isKabu ? 'object-contain bg-[#E0B87B]' : isCongelados ? 'object-contain bg-white' : isCrackPollo ? 'object-contain bg-white' : 'object-cover'}`} decoding="async" data-id="element-1637" /> : <img src="https://cdn.magicpatterns.com/uploads/66K7i7HxpjApVZJyeR9rjp/ChatGPT_Image_15_mar_2026,_13_53_22.png" alt="NOIZA" className="w-full h-full rounded-xl object-cover" decoding="async" data-id="element-1638" />}
          </div>

          {/* Title & Info */}
          <div className="flex-1 pb-2" data-id="element-1639">
            <h1 className={`text-2xl sm:text-4xl font-black ${isNatuCafe ? 'text-[#F5F0EB]' : isCongelados ? 'text-[#7B2D8E]' : isCabanaParrillera ? 'text-[#FFB347]' : 'text-[#3D2B1F]'} mb-1 tracking-tight`} style={{
            fontFamily: isNatuCafe ? "'Playfair Display', serif" : isCongelados ? "'Nunito', sans-serif" : "'Playfair Display', serif"
          }} data-id="element-1640">
              {restaurant.name} {isCongelados && '🍦'}{' '}
              {isCabanaParrillera && '🔥'}
            </h1>
            {restaurant.description_short && <p className={`${isNatuCafe ? 'text-[#C6A969]' : isCongelados ? 'text-[#5D3A1A]' : isCabanaParrillera ? 'text-[#E8C9A0]' : 'text-[#8B7355]'} text-sm sm:text-base max-w-2xl mb-3`} data-id="element-1641">
                {restaurant.description_short}
              </p>}

            <div className={`flex flex-wrap items-center gap-3 text-sm ${isNatuCafe ? 'text-[#8B7B6B]' : isCongelados ? 'text-[#7B2D8E]/80' : isCabanaParrillera ? 'text-[#A89682]' : 'text-[#8B7355]/70'} font-medium`} data-id="element-1642">
              {(zoneName || restaurant.address_text) && <div className="flex items-center gap-1.5" data-id="element-1643">
                  <MapPin className="w-4 h-4" data-id="element-1644" />
                  {zoneName || restaurant.address_text}
                </div>}

              {settings?.average_prep_time_minutes && <div className="flex items-center gap-1.5" data-id="element-1645">
                  <Clock className="w-4 h-4" data-id="element-1646" />
                  {settings.average_prep_time_minutes} min
                </div>}

              {settings?.minimum_order_amount != null && settings.minimum_order_amount > 0 && <div className="flex items-center gap-1.5" data-id="element-1647">
                    <Info className="w-4 h-4" data-id="element-1648" />
                    Mín. {formatCurrency(settings.minimum_order_amount)}
                  </div>}
            </div>
          </div>
        </div>

        {/* Order Methods */}
        {settings && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-1649">
            {settings.accept_delivery && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isNatuCafe ? 'linear-gradient(135deg, #C6A969 0%, #D4883A 100%)' : isCongelados ? 'linear-gradient(135deg, #7B2D8E 0%, #F472B6 100%)' : isCrackPollo ? 'linear-gradient(135deg, #F27B20 0%, #F5B731 100%)' : isCabanaParrillera ? 'linear-gradient(135deg, #FF6B35 0%, #F59E0B 100%)' : 'linear-gradient(135deg, #006847 0%, #2AABB3 100%)'
        }} data-id="element-1650">
                <Bike className="w-4 h-4" data-id="element-1651" /> {t('delivery')}
              </span>}
            {settings.accept_pickup && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isNatuCafe ? 'linear-gradient(135deg, #D4883A 0%, #8B5E3C 100%)' : isCongelados ? 'linear-gradient(135deg, #F472B6 0%, #D4A24E 100%)' : isCrackPollo ? 'linear-gradient(135deg, #F5B731 0%, #F27B20 100%)' : isCabanaParrillera ? 'linear-gradient(135deg, #F59E0B 0%, #DC2626 100%)' : 'linear-gradient(135deg, #CE1126 0%, #E8751A 100%)'
        }} data-id="element-1652">
                <ShoppingBag className="w-4 h-4" data-id="element-1653" /> {t('pickup')}
              </span>}
            {((settings as any).accept_dine_in || isCongelados || isNatuCafe || isCrackPollo || isCabanaParrillera) && <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white border border-white/10 shadow-sm" style={{
          background: isNatuCafe ? 'linear-gradient(135deg, #8B5E3C 0%, #C6A969 100%)' : isCongelados ? 'linear-gradient(135deg, #4A7FD4 0%, #7B2D8E 100%)' : isCrackPollo ? 'linear-gradient(135deg, #E86A10 0%, #F27B20 100%)' : isCabanaParrillera ? 'linear-gradient(135deg, #DC2626 0%, #7C2D12 100%)' : 'linear-gradient(135deg, #6B2FA0 0%, #CE1126 100%)'
        }} data-id="element-1654">
                <UtensilsCrossed className="w-4 h-4" data-id="element-1655" />{' '}
                {isCabanaParrillera ? 'Comer en el local' : t('dine_in')}
              </span>}
          </div>}

        {/* Action buttons — Congelados specific */}
        {isCongelados && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-1656">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #7B2D8E 0%, #4A7FD4 100%)'
        }} data-id="element-1657">
                <Navigation className="w-4 h-4" data-id="element-1658" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => setShowSchedule(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#7B2D8E] border-2 border-[#7B2D8E]/30 bg-white shadow-sm transition-transform active:scale-95 hover:bg-[#7B2D8E]/5" data-id="element-1659">
              <CalendarClock className="w-4 h-4" data-id="element-1660" />
              Horario
            </button>
          </div>}

        {/* NatuCafé action buttons */}
        {isNatuCafe && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-1661">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#0F0D0B] shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #C6A969 0%, #D4883A 100%)'
        }} data-id="element-1662">
                <Navigation className="w-4 h-4" data-id="element-1663" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => {
          alert('☕ Horario de NatuCafé Coffee Shop:\n\nLunes a Miércoles: 11:00 AM – 7:00 PM\nJueves a Domingo: 11:00 AM – 8:00 PM\n\n📍 Los Santos, Costa Rica');
        }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#C6A969] border-2 border-[#C6A969]/30 bg-transparent shadow-sm transition-transform active:scale-95 hover:bg-[#C6A969]/10" data-id="element-1664">
              <CalendarClock className="w-4 h-4" data-id="element-1665" />
              Horario
            </button>
          </div>}

        {/* Crack Pollo action buttons */}
        {isCrackPollo && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-1666">
            <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || 'Crack Pollo Los Santos Costa Rica')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #F27B20 0%, #F5B731 100%)'
        }} data-id="element-1667">
              <Navigation className="w-4 h-4" data-id="element-1668" />
              ¿Cómo llegar?
            </a>
            <button onClick={() => {
          alert("🍗 Horario de Crack'pollö:\n\nLunes a Sábado: 10:00 AM – 10:00 PM\nDomingo: Cerrado\n\n📍 Los Santos, Costa Rica");
        }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#F27B20] border-2 border-[#F27B20]/30 bg-white shadow-sm transition-transform active:scale-95 hover:bg-[#F27B20]/5" data-id="element-1669">
              <CalendarClock className="w-4 h-4" data-id="element-1670" />
              Horario
            </button>
          </div>}

        {/* La Cabaña Parrillera action buttons */}
        {isCabanaParrillera && <div className="flex flex-wrap gap-2.5 pb-5" data-id="element-1671">
            {(restaurant.lat && restaurant.lng || restaurant.address_text) && <a href={restaurant.lat && restaurant.lng ? `https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}` : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(restaurant.address_text || 'La Cabaña Parrillera Costa Rica')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-white shadow-md transition-transform active:scale-95" style={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #F59E0B 100%)'
        }} data-id="element-1672">
                <Navigation className="w-4 h-4" data-id="element-1673" />
                ¿Cómo llegar?
              </a>}
            <button onClick={() => setShowSchedule(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold text-[#FFB347] border-2 border-[#FFB347]/40 bg-transparent shadow-sm transition-transform active:scale-95 hover:bg-[#FFB347]/10" data-id="element-1674">
              <CalendarClock className="w-4 h-4" data-id="element-1675" />
              Horario
            </button>
          </div>}
      </div>

      {/* Congelados Schedule Modal */}
      {showSchedule && isCongelados && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowSchedule(false)} data-id="element-1676">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-id="element-1677" />
          <div className="relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #FBF5FF 0%, #F3E8FA 100%)'
      }} data-id="element-1678">
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 text-center" style={{
          background: 'linear-gradient(135deg, #7B2D8E 0%, #9B4DCA 50%, #F472B6 100%)'
        }} data-id="element-1679">
              <button onClick={() => setShowSchedule(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors" data-id="element-1680">
                <X className="w-4 h-4" data-id="element-1681" />
              </button>
              <span className="text-4xl mb-2 block" data-id="element-1682">🍦</span>
              <h3 className="text-xl font-black text-white tracking-tight" style={{
            fontFamily: "'Nunito', sans-serif"
          }} data-id="element-1683">
                Nuestro Horario
              </h3>
              <p className="text-white/80 text-xs mt-1" data-id="element-1684">Heladería Congelados</p>
            </div>

            {/* Schedule */}
            <div className="px-6 py-5 space-y-2" data-id="element-1685">
              {[{
            day: 'Lunes',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Martes',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Miércoles',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Jueves',
            hours: '10:00 AM – 5:00 PM'
          }, {
            day: 'Viernes',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Sábado',
            hours: '10:00 AM – 6:00 PM'
          }, {
            day: 'Domingo',
            hours: '1:00 PM – 6:00 PM'
          }].map(({
            day,
            hours
          }) => {
            const today = new Date().toLocaleDateString('es-CR', {
              weekday: 'long'
            });
            const isToday = today.toLowerCase() === day.toLowerCase();
            return <div key={day} className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-colors ${isToday ? 'bg-[#7B2D8E]/10 border border-[#7B2D8E]/20' : 'bg-white/60'}`} data-id="element-1686">
                    <span className={`font-bold ${isToday ? 'text-[#7B2D8E]' : 'text-[#5D3A1A]'}`} data-id="element-1687">
                      {isToday ? `📍 ${day}` : day}
                    </span>
                    <span className={`font-semibold ${isToday ? 'text-[#7B2D8E]' : 'text-[#5D3A1A]/70'}`} data-id="element-1688">
                      {hours}
                    </span>
                  </div>;
          })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6" data-id="element-1689">
              <button onClick={() => setShowSchedule(false)} className="w-full py-3 rounded-2xl text-sm font-bold text-white shadow-lg transition-transform active:scale-95" style={{
            background: 'linear-gradient(135deg, #7B2D8E 0%, #F472B6 100%)'
          }} data-id="element-1690">
                ¡Te esperamos! 🍨
              </button>
            </div>
          </div>
        </div>}

      {/* La Cabaña Parrillera Schedule Modal */}
      {showSchedule && isCabanaParrillera && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowSchedule(false)} data-id="element-1691">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-1692" />
          <div className="relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden border border-[#3A1F0F]" onClick={e => e.stopPropagation()} style={{
        background: 'linear-gradient(180deg, #1A0B05 0%, #0B0807 100%)'
      }} data-id="element-1693">
            {/* Header */}
            <div className="relative px-6 pt-6 pb-4 text-center" style={{
          background: 'linear-gradient(135deg, #7C2D12 0%, #DC2626 40%, #FF6B35 75%, #F59E0B 100%)'
        }} data-id="element-1694">
              <button onClick={() => setShowSchedule(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 transition-colors" data-id="element-1695">
                <X className="w-4 h-4" data-id="element-1696" />
              </button>
              <span className="text-4xl mb-2 block" data-id="element-1697">🔥</span>
              <h3 className="text-xl font-black text-white tracking-tight" style={{
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-1698">
                Nuestro Horario
              </h3>
              <p className="text-white/85 text-xs mt-1" data-id="element-1699">La Cabaña Parrillera</p>
            </div>

            {/* Schedule */}
            <div className="px-6 py-5 space-y-2" data-id="element-1700">
              {[{
            day: 'Lunes',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Martes',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Miércoles',
            hours: 'Cerrado',
            open: false
          }, {
            day: 'Jueves',
            hours: '5:00 PM – 9:00 PM',
            open: true
          }, {
            day: 'Viernes',
            hours: '5:00 PM – 9:00 PM',
            open: true
          }, {
            day: 'Sábado',
            hours: '12:00 MD – 9:00 PM',
            open: true
          }, {
            day: 'Domingo',
            hours: '12:00 MD – 7:00 PM',
            open: true
          }].map(({
            day,
            hours,
            open
          }) => {
            const today = new Date().toLocaleDateString('es-CR', {
              weekday: 'long'
            });
            const isToday = today.toLowerCase() === day.toLowerCase();
            return <div key={day} className={`flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-colors border ${isToday ? 'bg-[#FF6B35]/15 border-[#FF6B35]/40' : 'bg-white/5 border-white/5'}`} data-id="element-1701">
                    <span className={`font-bold ${isToday ? 'text-[#FFB347]' : open ? 'text-[#E8C9A0]' : 'text-[#A89682]/60'}`} data-id="element-1702">
                      {isToday ? `🔥 ${day}` : day}
                    </span>
                    <span className={`font-semibold ${isToday ? 'text-[#FFB347]' : open ? 'text-[#E8C9A0]/85' : 'text-[#A89682]/50'}`} data-id="element-1703">
                      {hours}
                    </span>
                  </div>;
          })}
            </div>

            {/* Footer */}
            <div className="px-6 pb-6" data-id="element-1704">
              <button onClick={() => setShowSchedule(false)} className="w-full py-3 rounded-2xl text-sm font-bold text-white shadow-lg transition-transform active:scale-95" style={{
            background: 'linear-gradient(135deg, #DC2626 0%, #FF6B35 50%, #F59E0B 100%)'
          }} data-id="element-1705">
                ¡Te esperamos! 🔥
              </button>
            </div>
          </div>
        </div>}
    </div>;
}